import { ContentCopy, ExpandMore as Expand, PushPin, Share } from '@mui/icons-material';
import { Avatar, Box, CardContent, Chip, Collapse, Grid, GridProps, Link, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { green, grey, red, yellow } from '@mui/material/colors';
import React, { Dispatch, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';

import { CutterState, getCountryAvatar, getMarketAvatar, navigateTabsWithQueries } from "../types/cutterState";
import { CopyToClipboard } from '../common/CopyToClipBoard';
import { CutterLastConnDuration } from './CutterLastConnDuration';
import { ActiveMore } from '../common/PinIcon';
import { CuttersDetailsCarousel } from './CuttersDetailsCarousel';
import ToSearchLinks from '../common/ToSearchLinks';
import HelpIcon from '../help/HelpIcon';


const NoDataError: string = 'Plant data not available';
function cutterError(cutter: CutterState): boolean {

    return cutter.cusT_CompanyName === null
        || cutter.cusT_CompanyName === ''
        || cutter.cusT_CompanyId === '';
}


export interface CutterCardProps extends GridProps {
    cutter: CutterState,
    expandedstate: boolean,
    setexpand: (event: React.MouseEvent<HTMLButtonElement>) => void,
    favoritestate: boolean,
    setfavorite: (event: React.MouseEvent<HTMLButtonElement>) => void,
    activeslide: number,
    setactiveslide: Dispatch<SetStateAction<number>>
}

export const CutterCard: React.FC<CutterCardProps> = (props) => {
    const { 
        cutter, 
        expandedstate, 
        setexpand, 
        favoritestate, 
        setfavorite, 
        activeslide, 
        setactiveslide, 
        ...others 
    } = props;
    const navigate = useNavigate();
    // const linkType: React.HTMLAttributeAnchorTarget = '_top';
    const contracts = cutter.lisA_Contract ? cutter.lisA_Contract.replace("|", " / ") : 'No contract';
    const hasError = cutterError(cutter);
    return (
        <Grid container component={Box} {...others} sx={{
            borderRadius: '8px', boxShadow: 1,
            backgroundColor: (theme) => cutter.hasContract === 1 
                ? (theme.palette.mode === 'light' ? grey[200] : '#353535')
                : (theme.palette.mode === 'light' ? grey[100] : '#2d2d2d'),
            margin: 1, padding: 0
        }} key={cutter.serialNumber} >
            <CardContent component={Stack} direction="row" spacing={0} alignItems='center' sx={{
                paddingY: 0, paddingX: '2px', fontSize: { md: '0.8rem', xs: '0.6rem' }
            }}
                data-tut="tabSearch_Card" >
                <Link onClick={() => navigateTabsWithQueries(navigate, 'search', cutter.harD_Market)} sx={{ textDecoration: 'none' }}
                    href="#" title={`Search market '${cutter.harD_Market}'`} >
                    <Tooltip arrow title={cutter.harD_Market} placement="right-end" >
                        <Avatar sx={{ 
                            height: '24px', 
                            width: '24px', 
                            fontSize: '0.8rem', 
                            fontWeight: 'bold', 
                            bgcolor: getMarketAvatar(cutter).color,
                            color: '#ffffff'
                        }}
                            aria-label="recipe">
                            {getMarketAvatar(cutter).label}
                        </Avatar>
                    </Tooltip>
                </Link>
                <TextField variant="standard" helperText={
                    !cutter.cusT_CompanyName ? 
                    (<Typography sx={{ fontSize: '0.6rem', fontStyle: 'italic', fontWeight: 200 }} component="span" className={props.className} variant="body2">{NoDataError}</Typography>) 
                    : (<ToSearchLinks strInput={cutter.cusT_CompanyName ?? ''} whiteSpace="nowrap" sx={{ fontSize: '0.7rem', color: 'darkgrey', fontWeight: 400 }} />)
                } disabled error={hasError}
                    sx={{
                        "&  .MuiStack-root": {
                            marginY: 0,

                        },
                        "&  .MuiIconButton-root, .MuiSvgIcon-root": { // <--- here
                            fontSize: 'small',
                            paddingX: 0.2,
                            paddingY: 0,
                            fontWeight: 'bold'
                        },
                        "&  .MuiFormHelperText-root ": {
                            maxWidth: '15rem',
                            overflow: 'clip'
                        }
                    }}
                    slotProps={{
                        input: {
                            value: '',
                            startAdornment: (
                                <Box component={Stack} direction='row' spacing={0.1} alignItems='center' justifyContent='flex-start'>
                                    <Tooltip arrow title={contracts} placement="top" >
                                        <Link href="#" onClick={() => navigateTabsWithQueries(navigate, 'bookmarks', cutter.serialNumber)}
                                            sx={{
                                                fontSize: '1rem', fontWeight: 400, textDecoration: 'none', padding: 0,
                                                WebkitTextFillColor: (cutter.hasContract === 1 ? green[800] : yellow[600])
                                            }} >{cutter.serialNumber}</Link>
                                    </Tooltip>
                                    <div>&nbsp;</div>
                                    <CopyToClipboard
                                        TextToCopy={`${cutter.serialNumber}`} title='Copy to clipboard'
                                        AckMessage={`SerialNumber '${cutter.serialNumber}' copied to clipboard`}>
                                        <ContentCopy titleAccess="Copy SerialNumber" />
                                    </CopyToClipboard>
                                    <ActiveMore aria-label="more info" degrees={-90}
                                        active={favoritestate}
                                        onClick={e => setfavorite(e)}
                                        aria-expanded={favoritestate} data-tut="tabSearch_BtnBookmark" >
                                        <PushPin titleAccess={`${(!favoritestate ? 'Add to bookmarks' : 'Remove from bookmarks')}`}
                                            sx={{ fontSize: 'small', color: (favoritestate ? red[800] : 'inherit') }} />
                                    </ActiveMore>
                                    <HelpIcon helpContext="bookmark-add" placement="top" size="small" />
                                    <CopyToClipboard
                                        TextToCopy={`${window.location.origin}/bookmarks?serialnumber=${cutter.serialNumber}`} title='Copy to clipboard'
                                        AckMessage={'Cutter url copied to clipboard'}>
                                        <Share />
                                    </CopyToClipboard>
                                </Box>

                            ),
                            endAdornment: (
                                <Box component={Stack} direction='row' alignItems='center' justifyContent='flex-end'>
                                    <Tooltip arrow title={cutter.cusT_PlantName} placement="top" >
                                        <Link onClick={() => navigateTabsWithQueries(navigate, 'search', cutter.cusT_CompanyId)} href="#"
                                            sx={{
                                                fontSize: '0.7rem', fontWeight: 500,
                                                color: (theme) => theme.palette.mode === 'light' ? grey[700] : theme.palette.text.secondary,
                                                textDecoration: 'none', "&:hover": { textDecoration: 'underline' }, whiteSpace: "nowrap", overflow: 'hidden'
                                            }} underline='hover'>{cutter.cusT_CompanyId?.split(' ')[0]}</Link>
                                    </Tooltip>
                                    <CopyToClipboard
                                        TextToCopy={cutter.cusT_CompanyId} title='Copy to clipboard'
                                        AckMessage={`Company Id ${cutter.cusT_CompanyId} copied to clipboard`}>
                                        <ContentCopy sx={{ color: 'default', padding: 0, fontSize: 'small' }} titleAccess="Copy Company Id" />
                                    </CopyToClipboard>
                                </Box>
                            )
                        }
                    }}
                />
                {/* <> ==> Remove error : You have provided a `title` prop to the child of Tooltip */}
                <Link sx={{ textDecoration: 'none' }} onClick={() => navigateTabsWithQueries(navigate, 'search', cutter.lisA_Country)}
                    href="#" title={`Search cutters in ${cutter.lisA_Country}`}>
                    <Tooltip arrow title={cutter.lisA_Country} placement="right-end" >
                        <Avatar sx={{
                            width: '24px', height: '24px', fontSize: '1rem',
                            backgroundImage: getCountryAvatar(cutter),
                            backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'cover'
                        }} aria-label="recipe">
                            &nbsp;
                        </Avatar>
                    </Tooltip>
                </Link>
            </CardContent>
            <Box component={Stack} direction="row" justifyItems="space-between" display="flex" sx={{ marginY: 0.5, paddingY: 0, paddingX: 0, width: '100%', height: "24px" }} >
                <Chip label={cutter.harD_Machine_type} variant="outlined" clickable component="a"
                    onClick={() => navigateTabsWithQueries(navigate, 'search', cutter.harD_Machine_type)}
                    sx={{ 
                        backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[300] : theme.palette.action.selected,
                        fontWeight: 'bolder', width: 5 / 8, fontSize: '0.7rem', justifySelf: "flex-start" 
                    }}
                    size="small" />&nbsp;
                <CutterLastConnDuration cutter={cutter} navigate={navigate}
                    sx={{ justifySelf: "flex-end", width: 2.5 / 8, fontSize: '0.6rem', paddingX: 0, fontWeight: 'bolder' }}
                    size="small" />
                <ActiveMore aria-label="more info" degrees={180} sx={{ padding: 0, justifySelf: "flex-end" }} data-tut="tabSearch_BtnExpandDetails"
                    active={expandedstate} onClick={e => setexpand(e)} aria-expanded={expandedstate} >
                    <Expand />
                </ActiveMore>
                <HelpIcon helpContext="expand-details" placement="left" size="small" />
            </Box>
            <Collapse in={expandedstate} sx={{ padding: 0, width: '100%', display: 'block' }} timeout="auto">
                <CuttersDetailsCarousel autoPlay={false} cycleNavigation={false} navButtonsAlwaysVisible={false}
                    index={activeslide} onChange={index => { setactiveslide(index!); }}
                    cutter={cutter} sx={{ marginX: '1em', fontSize: '0.6rem', justifyContent: 'center', alignItems: 'center' }} />
            </Collapse>
        </Grid >
    );
};

export default CutterCard;